import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ActionItemKeys, NotificationResponse } from '@uplift/api';
import { trackSnoozeToDo, trackToDoCTA, useActionItem, useL10n } from '@uplift/hooks';
import { UserRole } from '@uplift/types';
import { Button, StackableCard } from '@uplift/ui-v2';

import skySrc from '../../../../../assets/images/sky.jpg';

const useSeeTherapistStyles = makeStyles()(theme => ({
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: theme.spacing(1),
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    }
  },
  background: {
    background: `url('${skySrc}') lightgray 50% / cover no-repeat`,
  },
  content: {
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3)
  }
}));

export const useSeeTherapistItem = () => {
  const seeTherapistItem = useActionItem(ActionItemKeys.CLIENT_SEE_THERAPIST);
  const { classes: seePsychClasses } = useSeeTherapistStyles();
  const l10n = useL10n();
  const [ hideItem, setHideItem ] = useState(false);

  const seePsychiatristActionItem = React.useMemo(() => {
    if (!seeTherapistItem.show) {
      return null;
    }

    if (seeTherapistItem.isLoading) {
      return null;
    }

    if (hideItem) {
      return null;
    }

    const handleMaybeLater = async () => {
      await seeTherapistItem.dismiss(NotificationResponse.later);
      trackSnoozeToDo({
        toDoType: ActionItemKeys.CLIENT_SEE_THERAPIST,
        userType: UserRole.patient
      });

      setHideItem(true);
    };

    const handleTracking = () => {
      trackToDoCTA({
        toDoType: ActionItemKeys.CLIENT_SEE_THERAPIST,
        userType: UserRole.patient
      });
    };

    return (
      <StackableCard className={seePsychClasses.background}>
        <Box className={seePsychClasses.content}>
          <Box fontWeight={600}>
            {l10n.dashboard.actionItems.seeTherapist.title}
          </Box>
          <Typography variant="body2" color="textPrimary">
            {l10n.dashboard.actionItems.seeTherapist.text}
          </Typography>
          <Box className={seePsychClasses.actions}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              component={Link}
              to="/matching"
              onClick={handleTracking}
            >
              {l10n.dashboard.actionItems.seeTherapist.matchButton}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleMaybeLater}
            >
              {l10n.dashboard.actionItems.seeTherapist.dismissButton}
            </Button>
          </Box>
        </Box>
      </StackableCard>
    );
  }, [ l10n, seePsychClasses.actions, seePsychClasses.background, seePsychClasses.content, seeTherapistItem ]);

  return {
    isLoading: seeTherapistItem.isLoading || seeTherapistItem.isDismissing,
    actionItem: seePsychiatristActionItem
  };
};
